// Column widths the Opportunities table is laid out with, and the table's minimum width derived from them.
// Every column is sized in px (spec 010 D7): a saved width must render exactly, so there is no percentage
// mode and no column that absorbs slack except the actions column. The defaults and minimums come from
// the design canvas (D11); three columns the canvas does not draw (Win Probability, Start Date,
// Opportunity Status) take their closest drawn equivalent.
import {OPPORTUNITY_COLUMNS, columnIdFor, type OpportunityColumnName} from './opportunityColumns';

export type ColumnWidth = {default: number; min: number};

export const NATIVE_COLUMN_WIDTHS: Record<OpportunityColumnName, ColumnWidth> = {
	name: {default: 320, min: 160},
	client: {default: 150, min: 90},
	primaryContact: {default: 190, min: 110},
	targetValue: {default: 130, min: 80},
	winProbability: {default: 130, min: 80}, // as Target Value
	stage: {default: 160, min: 100},
	labels: {default: 230, min: 120},
	startDate: {default: 130, min: 90}, // as End Date
	endDate: {default: 130, min: 90},
	// Wider than the canvas's 160/120: "No quote" plus the hover Create quote button measures ~169px (D11 overflow check).
	quoteStatus: {default: 180, min: 170},
	quoteExpiry: {default: 130, min: 90},
	// The canvas has no Opportunity Status column; the "Unconverted" pill measures ~126px, so 130 rather than 120.
	status: {default: 160, min: 130},
	salesperson: {default: 150, min: 90},
};

// Each custom field column's default and minimum. NOT the shared CUSTOM_FIELD_COLUMN_WIDTH_PX (160): that
// constant is also the Projects list's fixed width, and 010 changes only this list.
export const CUSTOM_FIELD_DEFAULT_WIDTH_PX = 170;
export const CUSTOM_FIELD_MIN_WIDTH_PX = 90;

// The row actions (kebab) column. The design-system ActionColumn fixes its own 78px minimum; in resize mode
// it takes up any spare width instead (D7).
export const ACTIONS_COLUMN_WIDTH_PX = 78;

/** A column's default width by design-system column id: a native column's canvas default, else the custom-field default. */
export function defaultWidthFor(id: string): number {
	const native = OPPORTUNITY_COLUMNS.find(c => columnIdFor(c.name) === id);
	return native ? NATIVE_COLUMN_WIDTHS[native.name].default : CUSTOM_FIELD_DEFAULT_WIDTH_PX;
}

/** The table's minimum width: the shown columns at their effective widths, plus the actions column. */
export function tableMinWidthPx(shown: Array<{id: string; width: number}>): number {
	return shown.reduce((sum, c) => sum + c.width, ACTIONS_COLUMN_WIDTH_PX);
}
